import moment from 'moment';
import { NOTIFICATION_TYPES } from '../constants/notificationTypes';

// Group similar notifications
export const groupNotifications = (notifications) => {
  const groups = {};
  
  notifications.forEach(notification => {
    // Safely handle undefined notification_type
    const notificationType = notification.notification_type || 'unknown';
    const category = notification.category || 'general';
    const key = `${notificationType}_${category}`;
    
    if (!groups[key]) {
      groups[key] = {
        ...notification,
        count: 1,
        items: [notification],
        isGroup: false
      };
    } else {
      groups[key].count++;
      groups[key].items.push(notification);
      groups[key].isGroup = true;
      groups[key].message = `${groups[key].count} new ${(notificationType || 'unknown').toLowerCase()} notifications`;
      groups[key].created_at = Math.max(
        new Date(groups[key].created_at).getTime(),
        new Date(notification.created_at).getTime()
      );
    }
  });
  
  return Object.values(groups);
};

// Sort notifications by priority and time
export const sortNotificationsByPriority = (notifications) => {
  return notifications.sort((a, b) => {
    const aType = NOTIFICATION_TYPES[a.priority || 'INFO'];
    const bType = NOTIFICATION_TYPES[b.priority || 'INFO'];
    
    // First sort by priority (lower number = higher priority)
    if (aType.priority !== bType.priority) {
      return aType.priority - bType.priority;
    }
    
    // Then sort by time (newer first)
    return new Date(b.created_at) - new Date(a.created_at);
  });
};

// Filter notifications
export const filterNotifications = {
  unreadOnly: (notifications) => notifications.filter(n => n.status === 0 || n.status === 'unread'),
  byPriority: (notifications, priority) => notifications.filter(n => n.priority === priority),
  byTimeRange: (notifications, hours) => {
    const cutoff = moment().subtract(hours, 'hours');
    return notifications.filter(n => moment(n.created_at).isAfter(cutoff));
  },
  byCategory: (notifications, category) => notifications.filter(n => n.category === category),
  recent: (notifications, limit = 5) => notifications.slice(0, limit)
};

// Get notification config
export const getNotificationConfig = (notification) => {
  const type = notification.priority || 'INFO';
  return NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.INFO;
};

// Format notification time
export const formatNotificationTime = (timestamp) => {
  const now = moment();
  const time = moment(timestamp);
  
  if (now.diff(time, 'minutes') < 1) {
    return 'Just now';
  } else if (now.diff(time, 'hours') < 1) {
    return `${now.diff(time, 'minutes')}m ago`;
  } else if (now.diff(time, 'days') < 1) {
    return `${now.diff(time, 'hours')}h ago`;
  } else if (now.diff(time, 'days') < 7) {
    return `${now.diff(time, 'days')}d ago`;
  } else {
    return time.format('MMM DD');
  }
};

// Determine available actions for notification
export const getAvailableActions = (notification) => {
  const actions = ['VIEW', 'DISMISS'];
  
  // Add specific actions based on notification type
  if (notification.category === 'approval' && notification.status !== 'read') {
    actions.splice(1, 0, 'APPROVE');
  }
  
  // Add snooze for non-urgent notifications
  if (notification.priority !== 'URGENT' && notification.status !== 'read') {
    actions.splice(-1, 0, 'SNOOZE');
  }
  
  // Add defer for important notifications
  if (notification.priority === 'INFO' || notification.priority === 'IMPORTANT') {
    actions.splice(-1, 0, 'DEFER');
  }
  
  return actions;
};